/**
 * Self-service account closure, the pages a signed-out person sees.
 *
 *   /account-closure/closed   right after closing: what happens now and until when it can be undone
 *   /account-closure/cancel   the "Keep my account" link Identity e-mails when an account is closed
 *
 * `/account-closure/cancel` is part of the contract with authentication-service (its e-mail links to it). It
 * acts only on an explicit button press, never on load, so a mail scanner that opens links cannot restore an
 * account for the owner.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router";

import { AuthLayout } from "@/components/layout";
import { Alert, Button, buttonClass, ErrorAlert } from "@/components/ui";
import { auth, session } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { ApiError } from "@/lib/http";

const LINK_CLASS = "text-brand-700 hover:underline dark:text-brand-500";

const isExpired = (err: unknown) => err instanceof ApiError && err.reason === "ACCOUNT_CLOSURE_EXPIRED";
const isDeadLink = (err: unknown) =>
  err instanceof ApiError && (err.reason === "TOKEN_INVALID_OR_EXPIRED" || err.status === 422);

function BackToSignIn() {
  return (
    <p className="mt-6 text-center text-sm">
      <Link to="/login" className={LINK_CLASS}>
        Back to sign in
      </Link>
    </p>
  );
}

// ── after closing ──────────────────────────────────────────────────────────
export function AccountClosedPage() {
  const [params] = useSearchParams();
  const until = params.get("until");
  const signOut = (useLocation().state as { signOut?: boolean } | null)?.signOut === true;
  const qc = useQueryClient();
  // Arriving from Settings → Close account: Identity has revoked every session, so forget this one too. Only
  // then, so opening this address directly never signs anybody out.
  useEffect(() => {
    if (!signOut) return;
    session.clear();
    qc.clear();
  }, [signOut, qc]);
  return (
    <AuthLayout title="Your account is closed">
      <div className="space-y-4 text-sm text-fg-muted">
        <p>You&apos;ve been signed out everywhere and can no longer sign in.</p>
        <p>
          {until ? (
            <>
              Your personal data will be anonymised for good on{" "}
              <strong className="text-fg">{formatDateTime(until)}</strong>.
            </>
          ) : (
            "Your personal data will be anonymised for good when the grace period ends."
          )}{" "}
          Until then you can change your mind: we&apos;ve e-mailed you a link that restores your account
          exactly as it was.
        </p>
        <p>If you didn&apos;t mean to do this, use that link now.</p>
      </div>
      <BackToSignIn />
    </AuthLayout>
  );
}

// ── keep my account (the e-mailed link) ────────────────────────────────────
export function AccountClosureCancelPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const restore = useMutation({ mutationFn: () => auth.cancelAccountClosure(token) });
  return (
    <AuthLayout title="Keep your account">
      {!token && <Alert tone="danger">This link is incomplete.</Alert>}
      {token && !restore.isSuccess && (
        <div className="space-y-4">
          <p className="text-sm text-fg-muted">
            Your account was closed and is waiting to be anonymised. Keep it, and everything comes back
            exactly as it was: your businesses, your team access and your settings.
          </p>
          {isExpired(restore.error) ? (
            <Alert tone="danger">
              The time to restore this account has run out, and it can no longer be kept. You&apos;re welcome
              to{" "}
              <Link to="/register" className="font-medium underline">
                create a new account
              </Link>
              .
            </Alert>
          ) : isDeadLink(restore.error) ? (
            <Alert tone="danger">
              This link no longer works. The account may already be active again, or the link may have been
              used. Try{" "}
              <Link to="/login" className="font-medium underline">
                signing in
              </Link>
              .
            </Alert>
          ) : (
            <ErrorAlert error={restore.error} />
          )}
          <Button className="w-full" loading={restore.isPending} onClick={() => restore.mutate()}>
            Keep my account
          </Button>
        </div>
      )}
      {restore.isSuccess && (
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto size-10 text-brand-600" aria-hidden />
          <p className="text-sm text-fg-muted">Your account is active again. Nothing was changed.</p>
          <Link to="/login" className={buttonClass("primary", "md", "w-full")}>
            Sign in
          </Link>
        </div>
      )}
      {!restore.isSuccess && <BackToSignIn />}
    </AuthLayout>
  );
}
